import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
    Alert, Box, Button, ButtonBase, Chip, Divider, IconButton, MenuItem,
    Paper, Stack, Switch, Tab, Tabs, TextField, Typography,
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import BarChartIcon from '@mui/icons-material/BarChart';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { ConfigCard } from '../style/cards';
import { PdiRow as Row } from '../style/cards/_PdiHelpers';
import { useAnchor } from '../search/WebshopConfigSearch';

// ────────────────────────────────────────────────────────────────────────
// Static option pools
// ────────────────────────────────────────────────────────────────────────
const COOKIE_CATEGORIES = [
    { key: 'necessary', icon: <LockOutlinedIcon     fontSize="small" />, required: true,
      i18nKey: 'cfg_overige_cookie_cat_necessary',
      titleFallback: 'Necessary & functional',
      descFallback:  'Essential for the shop to work — login, cart, checkout. Cannot be turned off.' },
    { key: 'analytics', icon: <BarChartIcon         fontSize="small" />, required: false,
      i18nKey: 'cfg_overige_cookie_cat_analytics',
      titleFallback: 'Analytics',
      descFallback:  'Help understand how visitors use the shop — page views, traffic sources, popular products.' },
    { key: 'marketing', icon: <CampaignOutlinedIcon fontSize="small" />, required: false,
      i18nKey: 'cfg_overige_cookie_cat_marketing',
      titleFallback: 'Marketing',
      descFallback:  'Used for targeted advertising, retargeting, and personalized content.' },
];

// Our friendly banner-style names map onto the vanilla-cookieconsent layouts:
// floating→cloud, modal→box, bottom→bar. The available positions depend on the
// layout (the storefront/old admin: bar = top|bottom, box/cloud = the 9
// vertical×horizontal combos).
const BANNER_STYLES = [
    { value: 'floating', i18nKey: 'cfg_overige_banner_style_floating', fallback: 'Floating cloud' },
    { value: 'modal',    i18nKey: 'cfg_overige_banner_style_modal',    fallback: 'Small modal'    },
    { value: 'bottom',   i18nKey: 'cfg_overige_banner_style_bottom',   fallback: 'Bottom bar'     },
];
const STYLE_TO_LAYOUT = { floating: 'cloud', modal: 'box', bottom: 'bar' };
const LAYOUT_TO_STYLE = { cloud: 'floating', box: 'modal', bar: 'bottom' };
const isBarStyle = (style) => style === 'bottom';

// Bar layout: only vertical. Box/cloud: a 3×3 grid of vertical×horizontal.
const POSITIONS_BAR = ['top', 'bottom'];
const VERTICALS = ['top', 'middle', 'bottom'];
const HORIZONTALS = ['left', 'center', 'right'];

// Coerce a stored position to a value valid for the given style.
const normalizePosition = (style, pos) => {
    const p = String(pos || '').trim();
    if (isBarStyle(style)) {
        return p.startsWith('top') ? 'top' : 'bottom';
    }
    const [v, h] = p.split(/\s+/);
    const vert = VERTICALS.includes(v) ? v : 'bottom';
    const horiz = HORIZONTALS.includes(h) ? h : 'right';
    return `${vert} ${horiz}`;
};

// Pre-defined tracking tools shoppers can plug an ID into. Anything else
// belongs in the "Custom code" card.
// `supported` = has a real backend key today (GTM_ID / PIXEL_ID). The other
// tools' ids have NO backend mapping yet, so their inputs are disabled (with a
// notice) to avoid users entering an id that silently vanishes on save.
const TRACKING_TOOLS = [
    { key: 'ga4',          i18nKey: 'cfg_overige_tool_ga4',         titleFallback: 'Google Analytics 4',  descFallback: 'Tracks page views, events, and traffic sources', badge: 'GA',  badgeBg: '#f57c00' },
    { key: 'metaPixel',    i18nKey: 'cfg_overige_tool_meta',        titleFallback: 'Meta Pixel',          descFallback: 'Facebook & Instagram ad tracking',               badge: 'M',   badgeBg: '#1976d2', supported: true },
    { key: 'tiktokPixel',  i18nKey: 'cfg_overige_tool_tiktok',      titleFallback: 'TikTok Pixel',        descFallback: 'TikTok advertising and conversion tracking',     badge: 'T',   badgeBg: '#000000' },
    { key: 'hotjar',       i18nKey: 'cfg_overige_tool_hotjar',      titleFallback: 'Hotjar',              descFallback: 'Heatmaps, session recordings, and feedback',     badge: 'H',   badgeBg: '#fd3a5c' },
    { key: 'gtm',          i18nKey: 'cfg_overige_tool_gtm',         titleFallback: 'Google Tag Manager',  descFallback: 'Container for managing third-party tags',        badge: 'GTM', badgeBg: '#0288d1', supported: true },
];

// ────────────────────────────────────────────────────────────────────────
// Small helpers
// ────────────────────────────────────────────────────────────────────────

// Visual mini-mockup used inside the banner-style and position pickers.
const StyleTile = ({ kind, position }) => {
    const muted = 'rgba(0,0,0,0.18)';
    const accent = 'currentColor';
    if (kind === 'floating') {
        return (
            <Box sx={{ width: 38, height: 26, border: `1px solid ${muted}`, borderRadius: 0.5, position: 'relative', backgroundColor: '#fff' }}>
                <Box sx={{ position: 'absolute', left: 4, right: 4, bottom: 4, height: 8, backgroundColor: accent, borderRadius: 0.4, boxShadow: '0 1px 2px rgba(0,0,0,0.15)' }} />
            </Box>
        );
    }
    if (kind === 'modal') {
        return (
            <Box sx={{ width: 38, height: 26, border: `1px solid ${muted}`, borderRadius: 0.5, position: 'relative', backgroundColor: '#fff' }}>
                <Box sx={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%,-50%)', width: 14, height: 12, backgroundColor: accent, borderRadius: 0.3 }} />
            </Box>
        );
    }
    if (kind === 'bottom') {
        return (
            <Box sx={{ width: 38, height: 26, border: `1px solid ${muted}`, borderRadius: 0.5, position: 'relative', backgroundColor: '#fff' }}>
                <Box sx={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 8, backgroundColor: accent }} />
            </Box>
        );
    }
    // position tile (top/bottom): box with bar at top OR bottom
    return (
        <Box sx={{ width: 38, height: 26, border: `1px solid ${muted}`, borderRadius: 0.5, position: 'relative', backgroundColor: '#fff' }}>
            <Box sx={{ position: 'absolute', left: 0, right: 0, [position === 'top' ? 'top' : 'bottom']: 0, height: 6, backgroundColor: accent }} />
        </Box>
    );
};
StyleTile.propTypes = { kind: PropTypes.string, position: PropTypes.string };

// Position picker — the available positions depend on the banner style:
//  • bar (our "bottom")  → two tiles: top / bottom (full-width strip).
//  • box/cloud           → a 3×3 grid of vertical×horizontal tiles, each a
//                          mini-frame with a dot in the matching corner/edge.
// Values match the storefront: 'top'|'bottom' for bar, '<vertical> <horizontal>'
// for box/cloud (e.g. 'bottom right').
const PositionDotTile = ({ vertical, horizontal, selected }) => {
    const accent = selected ? 'rgba(25,118,210,0.9)' : 'rgba(0,0,0,0.55)';
    const vAlign = vertical === 'top' ? 'flex-start' : vertical === 'middle' ? 'center' : 'flex-end';
    const hAlign = horizontal === 'left' ? 'flex-start' : horizontal === 'center' ? 'center' : 'flex-end';
    return (
        <Box sx={{
            width: 34, height: 24, border: '1px solid', borderColor: selected ? 'primary.main' : 'rgba(0,0,0,0.18)',
            borderRadius: 0.5, backgroundColor: selected ? 'rgba(25,118,210,0.08)' : '#fff',
            display: 'flex', flexDirection: 'column', justifyContent: vAlign, alignItems: hAlign, p: '3px',
        }}>
            <Box sx={{ width: 12, height: 6, borderRadius: 0.3, backgroundColor: accent }} />
        </Box>
    );
};
PositionDotTile.propTypes = { vertical: PropTypes.string, horizontal: PropTypes.string, selected: PropTypes.bool };

const PositionPicker = ({ style, value, onChange, tr }) => {
    if (isBarStyle(style)) {
        return (
            <TilePicker
                options={POSITIONS_BAR.map(v => ({ value: v, label: tr(`cfg_overige_pos_${v}`, v === 'top' ? 'Top' : 'Bottom') }))}
                value={value}
                onChange={onChange}
                renderTile={(opt) => <StyleTile kind="position" position={opt.value} />}
            />
        );
    }
    // box/cloud → 3×3 grid
    return (
        <Box sx={{ display: 'inline-grid', gridTemplateColumns: 'repeat(3, auto)', gap: 0.75 }}>
            {VERTICALS.map(v => HORIZONTALS.map(h => {
                const val = `${v} ${h}`;
                const sel = value === val;
                return (
                    <ButtonBase
                        key={val}
                        onClick={() => onChange(val)}
                        title={tr(`cfg_overige_pos_${v}`, v) + ' ' + tr(`cfg_overige_pos_${h}`, h)}
                        sx={{ borderRadius: 0.75, p: 0.25 }}
                    >
                        <PositionDotTile vertical={v} horizontal={h} selected={sel} />
                    </ButtonBase>
                );
            }))}
        </Box>
    );
};
PositionPicker.propTypes = { style: PropTypes.string, value: PropTypes.string, onChange: PropTypes.func, tr: PropTypes.func };

// Tile picker — used both for banner-style and vertical-position. Selected
// tile gets the blue tint, unselected stays white-outlined.
const TilePicker = ({ options, value, onChange, renderTile }) => (
    <Box sx={{ display: 'flex', gap: 1 }}>
        {options.map(opt => {
            const sel = value === opt.value;
            return (
                <ButtonBase
                    key={opt.value}
                    onClick={() => onChange(opt.value)}
                    sx={{
                        flexDirection: 'column', gap: 0.5,
                        p: 1.25, minWidth: 96,
                        borderRadius: 1.5,
                        border: '1.5px solid',
                        borderColor: sel ? 'primary.main' : 'divider',
                        backgroundColor: sel ? 'rgba(25,118,210,0.08)' : '#fff',
                        color: sel ? 'primary.main' : 'text.secondary',
                        textTransform: 'none',
                        transition: 'all 0.12s',
                    }}
                >
                    {renderTile(opt)}
                    <Typography variant="caption" sx={{ fontWeight: 600, mt: 0.5 }}>{opt.label}</Typography>
                </ButtonBase>
            );
        })}
    </Box>
);
TilePicker.propTypes = { options: PropTypes.array, value: PropTypes.string, onChange: PropTypes.func, renderTile: PropTypes.func };

// ────────────────────────────────────────────────────────────────────────
// Main tab
// ────────────────────────────────────────────────────────────────────────
// Persisted-by-the-parent: the cookie-consent card (→ cookie.js via
// POST /admin/cookie-consent), customCode.head/body (→ _scriptsHeader/
// _scriptsBody) and the gtm/metaPixel tracking-tool ids (→ GTM_ID / PIXEL_ID).
// The ga4/tiktok/hotjar tools and the per-tool "active" flag have no confirmed
// backend yet — kept in the UI (pending backend check).
export const OVERIGE_CONFIG_DEFAULT = {
    cookies: {
        categories: { necessary: true, analytics: true, marketing: true },
        bannerStyle: 'bottom',
        position:    'bottom',
    },
    trackingTools: [],
    toolToAdd: '',
    customCode: {
        expanded: true,
        section: 'head', // 'head' | 'body'
        head: '',
        body: '',
    },
};

const OverigeTab = ({ theme, t, config: configProp, onConfigChange }) => {
    const tr = (key, fallback) => (typeof t === 'function' ? t(key, fallback) : fallback);

    const controlled = configProp != null && typeof onConfigChange === 'function';
    const [localConfig, setLocalConfig] = useState(OVERIGE_CONFIG_DEFAULT);
    const config = controlled ? configProp : localConfig;
    const setConfig = controlled ? onConfigChange : setLocalConfig;
    const setSection = (k) => (v) => setConfig(p => ({ ...p, [k]: { ...(p[k] || {}), ...v } }));

    const setCookies = setSection('cookies');
    const setCustomCode = setSection('customCode');
    // Search-jump target for the collapsible Custom-code panel (not a ConfigCard).
    const flashCustom = useAnchor('cfg-card-overige-custom');

    const toggleCategory = (key, value) => {
        setCookies({ categories: { ...config.cookies.categories, [key]: value } });
    };

    const addTool = () => {
        if (!config.toolToAdd) return;
        if (config.trackingTools.find(t => t.key === config.toolToAdd)) return;
        setConfig(p => ({
            ...p,
            trackingTools: [...p.trackingTools, { key: config.toolToAdd, id: '', active: true }],
            toolToAdd: '',
        }));
    };
    const removeTool = (key) => setConfig(p => ({ ...p, trackingTools: p.trackingTools.filter(t => t.key !== key) }));
    const updateTool = (key, patch) => setConfig(p => ({
        ...p,
        trackingTools: p.trackingTools.map(t => t.key === key ? { ...t, ...patch } : t),
    }));
    const availableTools = TRACKING_TOOLS.filter(t => !config.trackingTools.find(x => x.key === t.key));

    return (
        <Box>
            {/* Page header — matches the schema's "Cookies & tracking" title row */}
            <Box sx={{ mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {tr('cfg_overige_panel_title', 'Cookies & tracking')}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {tr('cfg_overige_panel_desc', 'Configure how cookie consent is asked from visitors, and manage tracking scripts like Google Analytics, Meta Pixel, or your own custom code.')}
                </Typography>
            </Box>

            <Stack spacing={2}>
                {/* ────── Cookie consent ────── */}
                <ConfigCard
                    anchor="cfg-card-overige-consent"
                    title={tr('cfg_overige_consent_title', 'Cookie consent')}
                    description={tr('cfg_overige_consent_desc', 'What visitors see when they first arrive on your site. Required for GDPR and other privacy regulations.')}
                    theme={theme}
                >
                    {/* Categories */}
                    <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {tr('cfg_overige_cat_title', 'Cookie categories visitors can choose from')}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1.25, lineHeight: 1.4 }}>
                            {tr('cfg_overige_cat_help', 'Visitors can opt in or out of these categories. Necessary cookies are always required for the site to function.')}
                        </Typography>
                        <Stack spacing={1}>
                            {COOKIE_CATEGORIES.map(cat => {
                                const checked = config.cookies.categories[cat.key];
                                return (
                                    <Paper key={cat.key} variant="outlined" sx={{
                                        p: 1.5, borderRadius: 1.5,
                                        backgroundColor: cat.required ? 'rgba(25,118,210,0.08)' : '#fff',
                                        borderColor: cat.required ? 'primary.light' : 'divider',
                                        display: 'flex', alignItems: 'center', gap: 1.5,
                                    }}>
                                        <Box sx={{
                                            width: 32, height: 32, borderRadius: 1,
                                            backgroundColor: 'rgba(0,0,0,0.04)',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            color: 'text.secondary', flexShrink: 0,
                                        }}>{cat.icon}</Box>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                                    {tr(cat.i18nKey, cat.titleFallback)}
                                                </Typography>
                                                {cat.required && (
                                                    <Chip
                                                        label={tr('cfg_overige_required', 'REQUIRED')}
                                                        size="small"
                                                        color="primary"
                                                        sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700, '& .MuiChip-label': { px: 0.75 } }}
                                                    />
                                                )}
                                            </Box>
                                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.35 }}>
                                                {tr(`${cat.i18nKey}_desc`, cat.descFallback)}
                                            </Typography>
                                        </Box>
                                        <Switch
                                            color="success"
                                            size="small"
                                            checked={!!checked}
                                            disabled={cat.required}
                                            onChange={(e) => toggleCategory(cat.key, e.target.checked)}
                                        />
                                    </Paper>
                                );
                            })}
                        </Stack>
                    </Box>

                    <Divider />

                    <Row
                        label={tr('cfg_overige_banner_style', 'Banner style')}
                        help={tr('cfg_overige_banner_style_help', 'How the consent prompt appears to visitors. Click to preview each option.')}
                        align="top"
                        control={
                            <TilePicker
                                options={BANNER_STYLES.map(o => ({ ...o, label: tr(o.i18nKey, o.fallback) }))}
                                value={config.cookies.bannerStyle}
                                onChange={(v) => setCookies({ bannerStyle: v, position: normalizePosition(v, config.cookies.position) })}
                                renderTile={(opt) => <StyleTile kind={opt.value} />}
                            />
                        }
                    />

                    <Divider />

                    <Row
                        label={tr('cfg_overige_position', 'Position')}
                        help={tr('cfg_overige_position_help', 'Where the prompt sits on the page. The available positions depend on the banner style above.')}
                        align="top"
                        control={
                            <PositionPicker
                                style={config.cookies.bannerStyle}
                                value={config.cookies.position}
                                onChange={(v) => setCookies({ position: v })}
                                tr={tr}
                            />
                        }
                    />

                    <Divider />

                    {/* Live preview */}
                    <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {tr('cfg_overige_preview_title', 'Live preview')}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1, lineHeight: 1.4 }}>
                            {tr('cfg_overige_preview_help', 'Updates as you change the banner style and position above')}
                        </Typography>
                        <CookiePreview
                            style={config.cookies.bannerStyle}
                            position={config.cookies.position}
                            tr={tr}
                        />
                    </Box>
                </ConfigCard>

                {/* ────── Tracking tools ────── */}
                <ConfigCard
                    anchor="cfg-card-overige-tools"
                    title={tr('cfg_overige_tools_title', 'Tracking tools')}
                    description={tr('cfg_overige_tools_desc', "Add common analytics and marketing tools by entering just an ID — we generate the script tags for you. For tools not in this list, use the 'Custom code' section below.")}
                    theme={theme}
                >
                    <Stack spacing={1}>
                        {config.trackingTools.map(t => {
                            const meta = TRACKING_TOOLS.find(x => x.key === t.key);
                            if (!meta) return null;
                            const supported = !!meta.supported;
                            return (
                                <Paper key={t.key} variant="outlined" sx={{
                                    p: 1.25, borderRadius: 1.5,
                                    backgroundColor: 'rgba(25,118,210,0.06)',
                                    borderColor: 'primary.light',
                                    display: 'grid',
                                    gridTemplateColumns: 'auto 1fr 220px auto auto',
                                    alignItems: 'center', gap: 1.5,
                                }}>
                                    <Box sx={{
                                        width: 36, height: 36, borderRadius: 1,
                                        backgroundColor: meta.badgeBg,
                                        color: '#fff',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        fontWeight: 700, fontSize: '0.85rem',
                                        flexShrink: 0,
                                    }}>{meta.badge}</Box>
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                            {tr(meta.i18nKey, meta.titleFallback)}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.35 }}>
                                            {tr(`${meta.i18nKey}_desc`, meta.descFallback)}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ minWidth: 0 }}>
                                        <TextField
                                            size="small"
                                            fullWidth
                                            value={t.id}
                                            onChange={(e) => updateTool(t.key, { id: e.target.value })}
                                            disabled={!supported}
                                            sx={{ backgroundColor: supported ? '#fff' : 'transparent', borderRadius: 1 }}
                                        />
                                        {!supported && (
                                            <Typography variant="caption" sx={{ color: 'warning.main', display: 'block', mt: 0.25, lineHeight: 1.2 }}>
                                                {tr('cfg_overige_tool_pending', 'Nog niet ondersteund — de ID wordt niet bewaard. Gebruik voorlopig Custom code.')}
                                            </Typography>
                                        )}
                                    </Box>
                                    <Chip
                                        label={supported
                                            ? (t.active ? tr('cfg_overige_active', 'Active') : tr('cfg_overige_inactive', 'Inactive'))
                                            : tr('cfg_overige_tool_soon', 'Binnenkort')}
                                        size="small"
                                        color={supported ? (t.active ? 'success' : 'default') : 'warning'}
                                        sx={{ height: 22, fontWeight: 600 }}
                                    />
                                    <IconButton size="small" onClick={() => removeTool(t.key)} sx={{ color: 'text.secondary' }}>
                                        <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                </Paper>
                            );
                        })}

                        {/* Add tool row */}
                        <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                            <TextField
                                select size="small" fullWidth
                                value={config.toolToAdd}
                                onChange={(e) => setConfig(p => ({ ...p, toolToAdd: e.target.value }))}
                                placeholder={tr('cfg_overige_choose_tool', 'Choose a tool to add…')}
                                SelectProps={{ displayEmpty: true }}
                            >
                                <MenuItem value="">
                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                        {tr('cfg_overige_choose_tool', 'Choose a tool to add…')}
                                    </Typography>
                                </MenuItem>
                                {availableTools.map(t => (
                                    <MenuItem key={t.key} value={t.key}>
                                        {tr(t.i18nKey, t.titleFallback)}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <Button
                                variant="contained"
                                onClick={addTool}
                                disabled={!config.toolToAdd}
                                sx={{ textTransform: 'none', flexShrink: 0 }}
                            >
                                {tr('cfg_overige_add_tool', 'Add tool')}
                            </Button>
                        </Box>
                    </Stack>
                </ConfigCard>

                {/* ────── Custom code (collapsible) ────── */}
                <Paper id="cfg-card-overige-custom" variant="outlined" sx={{
                    borderRadius: 2,
                    transition: 'box-shadow 0.25s ease, border-color 0.25s ease',
                    ...(flashCustom && { boxShadow: '0 0 0 3px rgba(0,113,187,0.4)', borderColor: 'rgba(0,113,187,0.8)' }),
                }}>
                    <ButtonBase
                        onClick={() => setCustomCode({ expanded: !config.customCode.expanded })}
                        sx={{
                            width: '100%', p: 2.5,
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            textAlign: 'left',
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {config.customCode.expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                {tr('cfg_overige_custom_title', 'Custom code')}
                            </Typography>
                        </Box>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {tr('cfg_overige_custom_subtitle', 'For advanced users — paste raw HTML, scripts, or meta tags')}
                        </Typography>
                    </ButtonBase>

                    {/* TODO/reminder — the custom-code mechanism is still being
                        reworked; this alert is intentionally always visible. */}
                    <Box sx={{ px: 2.5, pb: config.customCode.expanded ? 0 : 2.5 }}>
                        <Alert severity="info">
                            {tr('cfg_overige_custom_rework_notice', 'Note: how custom code is stored and applied is still being reworked — the behaviour of this section may change.')}
                        </Alert>
                    </Box>

                    {config.customCode.expanded && (
                        <Box sx={{ p: 2.5, pt: 0 }}>
                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1.5, lineHeight: 1.4 }}>
                                {tr('cfg_overige_custom_desc', 'Anything pasted here is added to your site\'s pages. The two tabs below correspond to the two places where scripts can run: in the document head (for tags loaded before the page renders) or just before the closing body tag (for tags that should load after the content).')}
                            </Typography>

                            <Alert severity="warning" sx={{ mb: 1.5 }}>
                                {tr('cfg_overige_custom_warning', 'Anything pasted here runs on every page of your site. Mistakes here can break the entire shop — only paste code you trust and have tested.')}
                            </Alert>

                            <Tabs
                                value={config.customCode.section}
                                onChange={(_e, v) => setCustomCode({ section: v })}
                                sx={{
                                    minHeight: 36,
                                    borderBottom: '1px solid', borderColor: 'divider',
                                    '& .MuiTab-root': { minHeight: 36, textTransform: 'none', fontSize: '0.85rem', fontWeight: 600 },
                                }}
                            >
                                <Tab
                                    value="head"
                                    label={
                                        <Box component="span">
                                            <Box component="code" sx={{ fontFamily: 'monospace', mr: 0.75 }}>{'<head>'}</Box>
                                            <Typography component="span" variant="caption" sx={{ color: 'text.secondary', fontWeight: 400 }}>
                                                — {tr('cfg_overige_custom_head_help', 'loads before the page renders')}
                                            </Typography>
                                        </Box>
                                    }
                                />
                                <Tab
                                    value="body"
                                    label={
                                        <Box component="span">
                                            <Box component="code" sx={{ fontFamily: 'monospace', mr: 0.75 }}>{'<body>'}</Box>
                                            <Typography component="span" variant="caption" sx={{ color: 'text.secondary', fontWeight: 400 }}>
                                                — {tr('cfg_overige_custom_body_help', 'loads at the end of the page')}
                                            </Typography>
                                        </Box>
                                    }
                                />
                            </Tabs>

                            <TextField
                                multiline
                                minRows={10}
                                fullWidth
                                value={config.customCode[config.customCode.section]}
                                onChange={(e) => setCustomCode({ [config.customCode.section]: e.target.value })}
                                placeholder={`<!-- Paste your ${config.customCode.section === 'head' ? '<head>' : '<body>'} code here -->`}
                                sx={{
                                    mt: 1.5,
                                    '& .MuiInputBase-root':  { p: 1.5 },
                                    '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.8rem', lineHeight: 1.5 },
                                }}
                            />
                        </Box>
                    )}
                </Paper>
            </Stack>
        </Box>
    );
};

OverigeTab.propTypes = { theme: PropTypes.string, t: PropTypes.func, config: PropTypes.object, onConfigChange: PropTypes.func };

// ────────────────────────────────────────────────────────────────────────
// Cookie banner live preview — fake browser frame with the banner inserted
// at the chosen position, in the chosen style.
// ────────────────────────────────────────────────────────────────────────
const CookiePreview = ({ style, position, tr }) => {
    // Position string → vertical/horizontal. Bar (our 'bottom') is full-width
    // and only top/bottom; box/cloud use the 3×3 vertical×horizontal grid.
    const isBar = style === 'bottom';
    const [posV, posH] = String(position || 'bottom').split(/\s+/);
    const vert  = ['top', 'middle', 'bottom'].includes(posV) ? posV : 'bottom';
    const horiz = ['left', 'center', 'right'].includes(posH) ? posH : 'right';

    const txt = tr('cfg_overige_preview_text', 'We use cookies to improve your shopping experience. Essential cookies are required; analytics and marketing are optional.');
    const title = tr('cfg_overige_preview_title_text', 'We use cookies');
    const lbl = {
        manage: tr('cfg_overige_preview_customize', 'Customize'),
        reject: tr('cfg_overige_preview_reject', 'Reject'),
        accept: tr('cfg_overige_preview_accept', 'Accept all'),
    };
    const miniBtn = (text, primary) => (
        <Box sx={{
            px: 1, py: 0.4, borderRadius: 0.75, textAlign: 'center', fontSize: '0.62rem', fontWeight: primary ? 700 : 600,
            ...(primary
                ? { backgroundColor: '#fff', color: '#000' }
                : { border: '1px solid rgba(255,255,255,0.35)' }),
        }}>{text}</Box>
    );

    // Three distinct live layouts:
    //  • bar (onderbalk)  → full-width strip, text left + inline buttons right.
    //  • cloud (wolk)     → floating rounded pill, text left + inline buttons,
    //                       compact (auto width) with shadow.
    //  • box (modal)      → compact card, title + text + STACKED buttons.
    let banner;
    if (isBar) {
        banner = (
            <Box sx={{
                backgroundColor: '#1a1a1a', color: '#fff', width: '100%',
                px: 1.5, py: 1, gap: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '0.72rem',
            }}>
                <Box sx={{ flex: 1, minWidth: 0, lineHeight: 1.3 }}>{txt}</Box>
                <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                    {miniBtn(lbl.manage)}{miniBtn(lbl.reject)}{miniBtn(lbl.accept, true)}
                </Box>
            </Box>
        );
    } else if (style === 'floating') {
        banner = (
            <Box sx={{
                backgroundColor: '#1a1a1a', color: '#fff',
                px: 1.75, py: 1.25, gap: 1.75, display: 'flex', alignItems: 'center', fontSize: '0.66rem',
                maxWidth: '94%', borderRadius: 2, boxShadow: '0 6px 16px rgba(0,0,0,0.3)',
            }}>
                <Box sx={{ minWidth: 0, maxWidth: 280 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.72rem', mb: 0.4 }}>{title}</Typography>
                    <Box sx={{ lineHeight: 1.3, opacity: 0.9 }}>{txt}</Box>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4, flexShrink: 0, minWidth: 92 }}>
                    {miniBtn(lbl.manage)}{miniBtn(lbl.reject)}{miniBtn(lbl.accept, true)}
                </Box>
            </Box>
        );
    } else {
        banner = (
            <Box sx={{
                backgroundColor: '#1a1a1a', color: '#fff',
                width: 168, p: 1.25, display: 'flex', flexDirection: 'column', gap: 0.75, fontSize: '0.62rem',
                borderRadius: 1, boxShadow: '0 3px 10px rgba(0,0,0,0.25)',
            }}>
                <Typography sx={{ fontWeight: 700, fontSize: '0.7rem' }}>{title}</Typography>
                <Box sx={{ lineHeight: 1.3, opacity: 0.9, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{txt}</Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4, mt: 0.25 }}>
                    {miniBtn(lbl.manage)}{miniBtn(lbl.reject)}{miniBtn(lbl.accept, true)}
                </Box>
            </Box>
        );
    }

    // Overlay aligns the banner inside the fake page: vertical from vert,
    // horizontal from horiz (bar stretches full-width so horizontal is moot).
    const alignItems = vert === 'top' ? 'flex-start' : vert === 'middle' ? 'center' : 'flex-end';
    const justifyContent = isBar ? 'stretch'
        : horiz === 'left' ? 'flex-start' : horiz === 'center' ? 'center' : 'flex-end';

    return (
        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', backgroundColor: 'rgba(25,118,210,0.04)', p: 2 }}>
            <Box sx={{ borderRadius: 1.5, border: '1px solid', borderColor: 'divider', backgroundColor: '#fff', overflow: 'hidden' }}>
                {/* Browser chrome */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1.5, py: 0.75, backgroundColor: 'rgba(0,0,0,0.04)', borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#ccc' }} />
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#ccc' }} />
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#ccc' }} />
                    <Box sx={{
                        ml: 1, flex: 1, backgroundColor: '#fff', borderRadius: 0.75,
                        px: 1.25, py: 0.4, border: '1px solid', borderColor: 'divider',
                        fontSize: '0.7rem', color: 'text.secondary',
                    }}>testshop.fashionretail.be</Box>
                </Box>

                {/* Page area — skeleton content with the banner overlaid at the
                    chosen position so all 9 placements read differently. */}
                <Box sx={{ position: 'relative', minHeight: 190 }}>
                    <Box sx={{ p: 2 }}>
                        <Box sx={{ height: 4, width: '90%', backgroundColor: 'rgba(0,0,0,0.06)', borderRadius: 0.4, mb: 0.75 }} />
                        <Box sx={{ height: 4, width: '60%', backgroundColor: 'rgba(0,0,0,0.06)', borderRadius: 0.4, mb: 0.75 }} />
                        <Box sx={{ height: 4, width: '75%', backgroundColor: 'rgba(0,0,0,0.06)', borderRadius: 0.4 }} />
                    </Box>
                    <Box sx={{
                        position: 'absolute', inset: isBar ? 0 : 8,
                        display: 'flex', alignItems, justifyContent,
                        pointerEvents: 'none',
                    }}>
                        {banner}
                    </Box>
                </Box>
            </Box>
        </Paper>
    );
};
CookiePreview.propTypes = { style: PropTypes.string, position: PropTypes.string, tr: PropTypes.func };

export default OverigeTab;
